/**
 * Board-game documentation links surfaced inside the game catalog.
 *
 * The docs site (`/wireframe`, `/rule`, `/plan`, `/changelog`) is served from
 * the same origin as this client, so the index is a plain same-origin fetch.
 * A game can be documented without being playable — those entries still belong
 * in the catalog, with the game itself inert and only the documents reachable.
 */

import type { GameManifest } from "@cloudnest/board-game-runtime";
import { useEffect, useState } from "react";

export const DOCS_INDEX_URL = "/wireframe/docs-index.json";

export interface GameDocLinks {
  readonly wireframe: string | null;
  readonly rule: string | null;
  readonly plan: string | null;
  readonly changelog: string | null;
}

export interface GameDocsEntry {
  readonly game: string;
  readonly displayName: string;
  readonly status: string;
  readonly docs: GameDocLinks;
}

export type GameDocsIndex = ReadonlyMap<string, GameDocsEntry>;

export const emptyGameDocsIndex: GameDocsIndex = new Map();

export interface CatalogEntry {
  readonly gameType: string;
  readonly displayName: string;
  /** `null` when the game is documented but not registered in the catalog. */
  readonly manifest: GameManifest | null;
  readonly docs: GameDocLinks | null;
  readonly docsStatus: string | null;
}

export const DOC_AXES = [
  { key: "wireframe", label: "와이어프레임", mark: "◧" },
  { key: "rule", label: "규칙", mark: "◎" },
  { key: "plan", label: "계획", mark: "◇" },
  { key: "changelog", label: "변경이력", mark: "◌" },
] as const;

export type DocAxisKey = (typeof DOC_AXES)[number]["key"];

/**
 * Loads the docs index published by the wireframe build.
 *
 * Documentation is an enhancement layer: when the index is unreachable (local
 * dev without the docs service, or a stale deploy) the catalog must still
 * render, so every failure resolves to an empty index rather than an error.
 */
export function useGameDocsIndex(): GameDocsIndex {
  const [index, setIndex] = useState<GameDocsIndex>(emptyGameDocsIndex);

  useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      try {
        const response = await fetch(DOCS_INDEX_URL);
        if (!response.ok) return;
        const payload = (await response.json()) as {
          readonly games?: readonly GameDocsEntry[];
        };
        if (!active) return;
        setIndex(
          new Map((payload.games ?? []).map((entry) => [entry.game, entry])),
        );
      } catch {
        // Keep the empty index; the catalog renders without doc shortcuts.
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, []);

  return index;
}

export function hasAnyDocLink(docs: GameDocLinks | null): boolean {
  if (!docs) return false;
  return DOC_AXES.some((axis) => docs[axis.key] !== null);
}

/**
 * Playable games keep their catalog order; documented-but-unplayable games are
 * appended so the list never hides work that exists only as documents.
 */
export function mergeCatalogWithDocs(
  games: readonly GameManifest[],
  docsIndex: GameDocsIndex,
): readonly CatalogEntry[] {
  const playableTypes = new Set(games.map((manifest) => manifest.gameType));

  const playable: readonly CatalogEntry[] = games.map((manifest) => {
    const entry = docsIndex.get(manifest.gameType);
    return {
      gameType: manifest.gameType,
      displayName: manifest.displayName,
      manifest,
      docs: entry?.docs ?? null,
      docsStatus: entry?.status ?? null,
    };
  });

  const documentedOnly: readonly CatalogEntry[] = [...docsIndex.values()]
    .filter((entry) => !playableTypes.has(entry.game))
    .sort((left, right) => left.game.localeCompare(right.game))
    .map((entry) => ({
      gameType: entry.game,
      displayName: entry.displayName,
      manifest: null,
      docs: entry.docs,
      docsStatus: entry.status,
    }));

  return [...playable, ...documentedOnly];
}
